import { child, defModule, emit, transitions, Update, type ModuleUpdate } from '@st/redux'
import type { BasicUser } from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { SdkClient, Snackbar } from '~/effects'
import { runSdkRequest } from '~/features/module-runtime'
import {
  organizerDraft,
  selCanSendClientOrganizer,
  selOrganizerEditorView,
  sendClientOrganizer,
  sendOrganizerEmail,
  stSendClientOrganizerModule,
  type OrganizerEmail,
  type OrganizerWrites,
  type STSendClientOrganizerMessage,
  type STSendClientOrganizerState
} from '../../st-send-client-organizer-module'
import { selSendClientOrganizerDialog } from '../../send-client-organizer-selectors'
import { formatFolderEntityName } from '../../st-folder-display'
import { isValidInvoiceUrl, setInvoiceRequest, type InvoiceLinkDraft } from './invoice-link'

const email = child(stSendClientOrganizerModule, 'email')

/**
 * Staff delivery of a tax return: the client email, an optional invoice link,
 * then a confirmation that offers the tax payment checklist.
 *
 * The email step is the organizer email editor. This module opens and closes
 * it and keeps the invoice draft beside it. It writes nothing: it hands the
 * delivery and draft saves to the folder, which runs them and records their
 * progress under the email draft, so leaving the binder neither cancels nor
 * strands them. Sending and the confirmation are read from that record. The
 * client sees an invoice only once its return is published, so a send that
 * fails after the invoice is saved shows the client nothing.
 */
export type DeliveryWizardState =
  | { step: 'closed' }
  | { step: 'email' | 'invoice'; invoice: InvoiceLinkDraft; email: STSendClientOrganizerState }

export type DeliveryWizardMessage =
  // Open the wizard on the email step for this draft delivery.
  | { type: 'opened'; folderId: string; users: BasicUser[]; contractId: string }
  // A message for the email editor.
  | { type: 'email'; message: STSendClientOrganizerMessage }
  // Move between the email and invoice steps.
  | { type: 'stepChanged'; step: 'email' | 'invoice' }
  // Show this invoice link or payment requirement.
  | { type: 'invoiceEdited'; edit: { url: string } | { required: boolean } }
  // Send the email, saving the invoice first if there is one.
  | { type: 'sendRequested' }
  // Close the wizard.
  | { type: 'closed' }

/** The writes the wizard asks the folder to run. */
export type DeliveryWizardOutput =
  // Save the invoice, if any, then publish the return and send the email.
  | { type: 'deliveryRequested'; email: OrganizerEmail; invoice: InvoiceLinkDraft }
  | { type: 'draftSaveRequested'; email: OrganizerEmail; closeAfter: boolean }

type DeliveryWizardUpdate = ModuleUpdate<
  DeliveryWizardState,
  DeliveryWizardMessage,
  DeliveryWizardOutput
>
type OpenWizardState = Extract<DeliveryWizardState, { step: 'email' | 'invoice' }>

export const initialDeliveryWizard: DeliveryWizardState = { step: 'closed' }

export const deliveryWizardModule = defModule<{
  state: DeliveryWizardState
  message: DeliveryWizardMessage
  output: DeliveryWizardOutput
}>({
  init: () => initialDeliveryWizard,
  tasks: (state) =>
    match(state)
      .with({ step: P.union('email', 'invoice') }, (state) => email.tasks(state.email))
      .otherwise(() => []),
  update: transitions('step', {
    closed: {
      opened: (_state, { folderId, users, contractId }) =>
        Update.mapState(
          email.init({
            folderId,
            users,
            dialog: 'sendEmail',
            target: { type: 'taxReturnDelivery', contractId }
          }),
          (email) => ({ step: 'email' as const, invoice: { url: '', required: false }, email })
        )
    },
    email: {
      email: (state, { message }) => updateEmail(state, message),
      stepChanged: (state, message) => ({ ...state, step: message.step }),
      closed: () => initialDeliveryWizard
    },
    invoice: {
      email: (state, { message }) => updateEmail(state, message),
      stepChanged: (state, message) => ({ ...state, step: message.step }),
      invoiceEdited: (state, message) => ({
        ...state,
        invoice: { ...state.invoice, ...message.edit }
      }),
      // The editor hands its email back as `emailSendRequested`.
      sendRequested: (state) => updateEmail(state, sendClientOrganizer()),
      closed: () => initialDeliveryWizard
    }
  })
})

function updateEmail(
  state: OpenWizardState,
  message: STSendClientOrganizerMessage
): DeliveryWizardUpdate {
  return email.update(state.email, message, (email) => ({ ...state, email }), {
    emailSendRequested: ({ email }, state) =>
      emit(state, { type: 'deliveryRequested', email, invoice: state.invoice }),
    draftSaveRequested: (output, state) => emit(state, output)
  })
}

/** The email draft the open wizard edits; the folder records its writes under it. */
export function selDeliveryDraft(state: DeliveryWizardState) {
  return state.step == 'closed' ? undefined : organizerDraft(state.email)
}

/**
 * The delivery's writes, run by the folder: the invoice is saved before the
 * return is published, and the email is sent last. Each failure is reported
 * here, so the user hears it even after leaving the binder.
 */
export function deliverReturn({
  email,
  invoice
}: Extract<DeliveryWizardOutput, { type: 'deliveryRequested' }>) {
  return Effect.gen(function* () {
    if (invoice.url.trim() != '') {
      const saved = yield* runSdkRequest(
        Effect.gen(function* () {
          const sdk = yield* SdkClient
          return yield* sdk.send(setInvoiceRequest(email.folderId, invoice))
        })
      )
      if (!saved.ok) {
        yield* Effect.logError('Failed to save delivery invoice', saved.error)
        const snackbar = yield* Snackbar
        yield* snackbar.show('Failed to save invoice link', { variant: 'error' })
        return 'failed' as const
      }
    }
    return yield* sendOrganizerEmail(email)
  })
}

/**
 * What the wizard shows: the email editor with delivery's heading, its invoice
 * step, or the confirmation once the folder records the delivery as sent.
 */
export function selDeliveryWizard(state: DeliveryWizardState, writes: OrganizerWrites) {
  if (state.step == 'closed') return { kind: 'closed' as const }
  const write = writes[organizerDraft(state.email)]
  const view = selOrganizerEditorView(state.email, write)
  if (view.status == 'sent') {
    return {
      kind: 'deliverySent' as const,
      clientName: formatFolderEntityName(view.sendState.folder.entities, 'client')
    }
  }
  if (state.step == 'invoice') {
    const sending = write?.status == 'sending'
    return {
      kind: 'deliveryInvoice' as const,
      invoice: state.invoice,
      busy: sending,
      canSend:
        !sending &&
        selCanSendClientOrganizer(view) &&
        (state.invoice.url.trim() == '' || isValidInvoiceUrl(state.invoice.url)),
      sendLabel: sending ? 'Sending' : 'Deliver the return'
    }
  }
  return match(selSendClientOrganizerDialog(view, 'stanfordtax'))
    .with({ kind: 'email' }, (model) => ({
      ...model,
      title: `Deliver return to ${formatFolderEntityName(
        model.state.sendState.folder.entities,
        'client'
      )}`,
      sendLabel: match(model.state.status)
        .with('loaded', () => 'Next')
        .otherwise(() => model.sendLabel)
    }))
    .otherwise((model) => model)
}
